import { Extension, mergeAttributes, Node } from "@tiptap/core"
import { TextSelection } from "@tiptap/pm/state"

import type { Editor } from "@tiptap/core"

declare module "@tiptap/core" {
	interface Commands<ReturnType> {
		mermaid: {
			insertMermaid: (source?: string) => ReturnType
		}
	}
}

export const MERMAID_PLACEMENTS = [
	"top-left",
	"top-right",
	"bottom-left",
	"bottom-right",
] as const

export const DEFAULT_MERMAID_SOURCE = "flowchart LR\n  A[Start] --> B[End]"

const LANGUAGE_CLASS = /(?:^|\s)language-mermaid(?:\s|$)/

// The server-side DOM has no `closest`, so the code-group check is optional.
function isMermaidPre(element: HTMLElement): boolean {
	if (element.getAttribute("data-language") === "mermaid") return true
	const code = element.querySelector?.("code")
	return LANGUAGE_CLASS.test(
		`${element.getAttribute("class") ?? ""} ${code?.getAttribute("class") ?? ""}`,
	)
}

const insideCodeGroup = (element: HTMLElement): boolean =>
	typeof element.closest === "function" &&
	Boolean(element.closest('[data-type="code-group"]'))

// A block whose Enter types a newline needs another way out when it ends the
// document, or the author is stuck inside it.
export function exitCodeLikeBlockAtEnd(
	editor: Editor,
	typeName: string,
	containerName: string = typeName,
): boolean {
	const { state } = editor
	const { selection } = state
	if (!selection.empty) return false
	const { $from } = selection
	if ($from.parent.type.name !== typeName) return false
	if ($from.parentOffset !== $from.parent.content.size) return false

	let depth = $from.depth
	while (depth > 0 && $from.node(depth).type.name !== containerName) depth -= 1
	if ($from.node(depth).type.name !== containerName) return false
	// Only the last thing in the container may exit, or the arrow skips content.
	for (let d = $from.depth; d > depth; d -= 1) {
		if ($from.index(d - 1) !== $from.node(d - 1).childCount - 1) return false
	}

	const after = $from.after(depth)
	if (after < state.doc.content.size) return false
	const paragraph = state.schema.nodes.paragraph
	if (!paragraph) return false
	return editor
		.chain()
		.command(({ tr }) => {
			tr.insert(after, paragraph.create())
			tr.setSelection(TextSelection.near(tr.doc.resolve(after + 1)))
			return true
		})
		.run()
}

// Mintlify's ```mermaid fence: the node's text is the source, which the portal
// draws client-side.
export const MermaidExtension = Node.create({
	name: "mermaid",
	group: "block",
	content: "text*",
	marks: "",
	code: true,
	defining: true,

	addAttributes() {
		return {
			placement: {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const value = element.getAttribute("data-placement")
					return MERMAID_PLACEMENTS.includes(
						value as (typeof MERMAID_PLACEMENTS)[number],
					)
						? value
						: null
				},
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.placement
						? { "data-placement": String(attributes.placement) }
						: {},
			},
			actions: {
				default: null,
				parseHTML: (element: HTMLElement) => {
					const value = element.getAttribute("data-actions")
					return value === "true" || value === "false" ? value : null
				},
				renderHTML: (attributes: Record<string, unknown>) =>
					attributes.actions === "true" || attributes.actions === "false"
						? { "data-actions": String(attributes.actions) }
						: {},
			},
		}
	},

	parseHTML() {
		return [
			{
				tag: 'div[data-type="mermaid"]',
				preserveWhitespace: "full",
				contentElement: (node) =>
					((node as HTMLElement).querySelector?.(
						"pre",
					) as HTMLElement | null) ?? (node as HTMLElement),
			},
			// A plain code block tagged mermaid (an older import, a pasted fence)
			// is a diagram too. Ahead of the code block's own rule.
			{
				tag: "pre",
				priority: 60,
				preserveWhitespace: "full",
				getAttrs: (node) => {
					const element = node as HTMLElement
					return isMermaidPre(element) && !insideCodeGroup(element)
						? null
						: false
				},
				contentElement: (node) =>
					((node as HTMLElement).querySelector?.(
						"code",
					) as HTMLElement | null) ?? (node as HTMLElement),
			},
		]
	},

	renderHTML({ HTMLAttributes }) {
		return [
			"div",
			mergeAttributes(HTMLAttributes, {
				"data-type": "mermaid",
				class: "pl-mermaid",
			}),
			["pre", { class: "pl-mermaid-source", "data-mermaid-source": "" }, 0],
		]
	},

	addCommands() {
		return {
			insertMermaid:
				(source = DEFAULT_MERMAID_SOURCE) =>
				({ chain, state }) => {
					const from = state.selection.from
					return chain()
						.insertContent({
							type: this.name,
							content: source ? [{ type: "text", text: source }] : [],
						})
						.command(({ tr, dispatch }) => {
							if (!dispatch) return true
							let target = -1
							tr.doc.descendants((node, pos) => {
								if (target >= 0) return false
								if (node.type.name === this.name && pos >= from - 1) {
									target = pos + node.nodeSize - 1
									return false
								}
								return true
							})
							if (target >= 0) {
								tr.setSelection(TextSelection.near(tr.doc.resolve(target)))
							}
							return true
						})
						.run()
				},
		}
	},
})

// Its own extension so it can run ahead of the shared keymaps (Mod-Enter is
// swallowed by DisableCommandEnter) without moving the node up the schema.
export const MermaidKeymap = Extension.create({
	name: "mermaidKeymap",
	priority: 1000,

	addKeyboardShortcuts() {
		return {
			ArrowDown: () => exitCodeLikeBlockAtEnd(this.editor, "mermaid"),
			"Mod-Enter": () => exitCodeLikeBlockAtEnd(this.editor, "mermaid"),
		}
	},
})

export const MermaidExtensions = [MermaidExtension, MermaidKeymap]
